import React, { FC, useEffect, useState } from 'react';
import { Asset, Button, Checkbox, CloseIcon, Modal } from '@clearcover/atlas';
import ReactMarkdown from 'react-markdown';

import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useAppContext } from 'QuoteApp/services/providers';
import { getErrorMessage } from 'QuoteApp/utils';

import styles from './styles.module.css';

type UWConsentModalProps = {
  open: boolean;
  onCancel: () => void;
  onAccepted: () => void;
};

export const UWConsentModal: FC<UWConsentModalProps> = ({
  open,
  onCancel,
  onAccepted,
}) => {
  const {
    appId,
    details,
    dictionary,
    refreshDetails,
    fetchRate,
  } = useAppContext();
  const { toast } = useToast();
  const [consented, setConsented] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [, postAcceptUW] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/underwriting_consent/complete`,
  });

  // Reset checkbox whenever the modal closes so it starts fresh next time
  useEffect(() => {
    if (!open) {
      setConsented(false);
    }
  }, [open]);

  const isRunnable = !!details.commands.completeUnderwritingConsent?.runnable;
  const underwritingMarkdown = dictionary?.underwritingConsent;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await postAcceptUW();
      await fetchRate();
      await refreshDetails();
      onAccepted();
    } catch (e) {
      toast({
        type: 'error',
        message: getErrorMessage(e),
        withSecondaryNav: false,
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      controlled
      open={open}
      variant="xl"
      handleClose={onCancel}
      showCloseIcon={false}
    >
      {closeModal => (
        <div className={styles.modal}>
          <div className={styles.header}>
            <h3 className={styles.title}>
              Before continuing, you must confirm the following information is
              true
            </h3>
            <button
              type="button"
              data-testid="close-uw-consent-modal"
              onClick={closeModal}
            >
              <Asset
                icon={CloseIcon}
                description="Close underwriting consent modal"
                height={16}
                width={16}
              />
            </button>
          </div>

          <div className={styles.content}>
            {underwritingMarkdown && (
              <ReactMarkdown className={styles.markdown} skipHtml>
                {underwritingMarkdown}
              </ReactMarkdown>
            )}
          </div>

          <div className={styles.footer}>
            <Checkbox
              data-testid="uw-consent-checkbox"
              name="uw-consent-modal"
              label="I confirm this information has been verified with the applicant, and that they agree to all of the statements above."
              value={consented}
              disabled={!isRunnable || isSubmitting}
              onChange={setConsented}
            />
            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                disabled={isSubmitting}
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!consented || !isRunnable || isSubmitting}
                isSubmitting={isSubmitting}
                onClick={handleConfirm}
                data-testid="uw-consent-confirm-button"
              >
                Confirm &amp; Switch to Direct Checkout
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
